import {Editor} from '@tiptap/react';
import React, {FC} from 'react';

import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {ToolbarButton} from '../../../../../../shared/ui/src/components/buttons/toolbarButton';
import {SimpleDropdown} from '../../../../../../shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../shared/ui/src/components/dropdowns/simpleDropdownItem';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {UserTheme} from '../../../../../../shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface ListButtonProps {
  editor: Editor;
}

/*
 * Component.
 */

export const ListButton: FC<ListButtonProps> = ({editor}) => {
  const theme = useUserTheme();
  const isActive = editor.isActive('bulletList') || editor.isActive('orderedList');
  return (
    <PopoverCoordinator
      renderButton={(isOpen) => renderButton(isOpen, isActive, theme)}
      renderModal={(modalProps) => renderDropdown(modalProps, editor)}
    />
  );
};

/*
 * Helpers.
 */

function renderButton(isOpen: boolean, isActive: boolean, theme: UserTheme) {
  return (
    <ToolbarButton
      iconName="formattingList"
      isActive={isActive}
      hasHover={isOpen}
      shouldPreventMouseDown
      label={<Trans id="knowledge-base-lists-button-label">Lists</Trans>}
      isOpen={isOpen}
      color={theme.greys.shade80}
      hoverColor={theme.greys.shade80}
    />
  );
}

function renderDropdown(modalProps: PopoverTransferProps, editor: Editor) {
  return (
    <SimpleDropdown {...modalProps}>
      <SimpleDropdownItem
        isChecked={editor.isActive('bulletList')}
        onSelect={() => editor.chain().focus().toggleBulletList().run()}
      >
        <Trans id="knowledge-base-list-button-item-bulleted">Bulleted List</Trans>
      </SimpleDropdownItem>
      <SimpleDropdownItem
        isChecked={editor.isActive('orderedList')}
        onSelect={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <Trans id="knowledge-base-list-button-item-numbered">Numbered List</Trans>
      </SimpleDropdownItem>
      <SimpleDropdownItem
        onSelect={() => {
          editor.chain().focus().sinkListItem('listItem').run();
        }}
      >
        <Trans id="knowledge-base-list-button-item-right">Indent Right</Trans>
      </SimpleDropdownItem>
      <SimpleDropdownItem
        onSelect={() => {
          editor.chain().focus().liftListItem('listItem').run();
        }}
      >
        <Trans id="knowledge-base-list-button-item-left">Indent Left</Trans>
      </SimpleDropdownItem>
    </SimpleDropdown>
  );
}
